import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { uploadFile } from '../../shared/service/fileUpload.service';
import { GENERIC_STAGES, loadProjectStages } from '../projectStages';
import {
  EngineerOption,
  Meta,
  ProjectOption,
  projectLabel,
  SHIFTS,
  Shift,
  siteVisitsApi,
  VisitBy,
  VisitDetail,
} from '../siteVisits.api';

const OTHER = 'Other';

interface Row {
  key: string;
  stage: string | null;
  title: string;
  hint: string | null;
  include: boolean;
  /** Rows the dialog built from a stage or the purpose; replaced when those change, until edited. */
  auto: boolean;
  touched: boolean;
}

interface Attachment {
  /** The visit's file row, once it is on the visit (edit mode). */
  rowId?: number;
  fileId: number;
  name: string;
  url: string;
}

let seq = 0;
const nextKey = () => `r${(seq += 1)}`;

const stageTitle = (stage: string) => `${stage}: work as per drawing`;
const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Schedule a site visit, or change one that is still only scheduled.
 *
 * The checklist builds itself: one "work as per drawing" item for each project stage ticked,
 * then the chosen purpose's template items. Rows the office edits (or adds) are theirs and
 * stay put when the stages or purpose change; untouched built rows are rebuilt.
 */
export default function ScheduleVisitDialog({
  open,
  meta,
  visit,
  onClose,
  onSaved,
}: {
  open: boolean;
  meta: Meta | null;
  /** Edit this visit; null schedules a new one. */
  visit: VisitDetail | null;
  onClose: () => void;
  onSaved: (visit: VisitDetail) => void;
}) {
  const editing = Boolean(visit);
  const [project, setProject] = useState<ProjectOption | null>(null);
  const [visitBy, setVisitBy] = useState<VisitBy>('SITE_ENGINEER');
  const [engineer, setEngineer] = useState<EngineerOption | null>(null);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [shift, setShift] = useState<Shift>('FIRST_HALF');
  const [purpose, setPurpose] = useState('');
  const [otherPurpose, setOtherPurpose] = useState('');
  const [stageOptions, setStageOptions] = useState<string[]>([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [stages, setStages] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [newItem, setNewItem] = useState('');
  const [instructions, setInstructions] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  /**
   * Set once the office picks stages or a purpose. Until then the rows are left as they are,
   * so opening an edit never re-adds template items that were taken off the visit.
   */
  const userChanged = useRef(false);

  const purposes = useMemo(() => (meta?.purposes ?? []).map((p) => p.purpose), [meta]);
  const purposeName = purpose === OTHER ? otherPurpose.trim() : purpose;

  // Fresh each time it opens, or filled from the visit being edited.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setNewItem('');
    userChanged.current = false;
    if (visit) {
      const p = meta?.projects.find((x) => x.id === visit.jobId) ?? {
        id: visit.jobId,
        code: visit.projectCode,
        name: visit.projectName,
        location: visit.projectLocation,
        siteAddress: visit.siteAddress,
        projectType: null,
        constructionWorkType: null,
        buildingType: null,
      };
      setProject(p);
      setVisitBy(visit.visitBy);
      setEngineer(meta?.engineers.find((e) => e.id === visit.engineerId) ?? null);
      setDate(visit.scheduledDate);
      setShift(visit.shift);
      const known = (meta?.purposes ?? []).some((x) => x.purpose === visit.purpose);
      setPurpose(visit.purpose ? (known ? visit.purpose : OTHER) : '');
      setOtherPurpose(known ? '' : visit.purpose ?? '');
      setStages(visit.stages ? visit.stages.split(',').map((s) => s.trim()).filter(Boolean) : []);
      setRows(
        visit.checks.map((c) => ({
          key: nextKey(),
          stage: c.stage,
          title: c.title,
          hint: c.hint,
          include: true,
          auto: false,
          touched: true,
        }))
      );
      setInstructions(visit.instructions ?? '');
      setAttachments(visit.attachments.map((a) => ({ rowId: a.id, fileId: a.fileId, name: a.name ?? 'File', url: a.url })));
    } else {
      setProject(null);
      setVisitBy('SITE_ENGINEER');
      setEngineer(null);
      setDate(dayjs().format('YYYY-MM-DD'));
      setShift('FIRST_HALF');
      setPurpose('');
      setOtherPurpose('');
      setStages([]);
      setRows([]);
      setInstructions('');
      setAttachments([]);
    }
  }, [open, visit, meta]);

  // The project's own stages, from its saved floors.
  useEffect(() => {
    if (!open || !project) {
      setStageOptions([]);
      return;
    }
    let live = true;
    setStagesLoading(true);
    loadProjectStages(project)
      .then((s) => live && setStageOptions(s))
      .catch((e) => {
        logError({ message: 'Could not load the project floors', error: e, code: 'SITE_VISIT_STAGES_FAILED' });
        if (live) setStageOptions(GENERIC_STAGES);
      })
      .finally(() => live && setStagesLoading(false));
    return () => {
      live = false;
    };
  }, [open, project]);

  // Rebuild the untouched built rows whenever the stages or the purpose change.
  useEffect(() => {
    if (!open || !userChanged.current) return;
    setRows((current) => {
      const kept = current.filter((r) => !r.auto || r.touched);
      const template = (meta?.purposes ?? []).find((p) => p.purpose === purpose)?.items ?? [];
      const built: Row[] = [
        ...stages.map((s) => ({ stage: s, title: stageTitle(s), hint: null })),
        ...template.map((t) => ({ stage: null, title: t.title, hint: t.hint })),
      ]
        .filter((b) => !kept.some((k) => same(k.title, b.title)))
        .map((b) => ({ ...b, key: nextKey(), include: true, auto: true, touched: false }));
      const builtStage = built.filter((b) => b.stage);
      const builtTemplate = built.filter((b) => !b.stage);
      return [...builtStage, ...builtTemplate, ...kept];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stages, purpose]);

  const editRow = (key: string, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch, touched: true } : r)));
  const removeRow = (key: string) => setRows((rs) => rs.filter((r) => r.key !== key));
  const moveRow = (index: number, by: number) =>
    setRows((rs) => {
      const target = index + by;
      if (target < 0 || target >= rs.length) return rs;
      const next = [...rs];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  const addItem = () => {
    const title = newItem.trim();
    if (!title) return;
    setRows((rs) => [...rs, { key: nextKey(), stage: null, title, hint: null, include: true, auto: false, touched: true }]);
    setNewItem('');
  };

  const toggleStage = (s: string) => {
    userChanged.current = true;
    setStages((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const up = await uploadFile(file);
        const fileId = Number(up.id);
        if (!fileId) throw new Error(`Upload of ${file.name} returned no id`);
        let rowId: number | undefined;
        if (visit) {
          const row = await siteVisitsApi.addFile(visit.id, { fileId, kind: 'ATTACHMENT' });
          rowId = row.id;
        }
        setAttachments((a) => [...a, { rowId, fileId, name: up.name ?? file.name, url: up.url ?? '' }]);
      }
    } catch (e) {
      toast.error(logError({ message: 'Could not upload the file', error: e, code: 'SITE_VISIT_ATTACH_FAILED' }));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const removeAttachment = async (a: Attachment) => {
    try {
      if (visit && a.rowId) await siteVisitsApi.deleteFile(visit.id, a.rowId);
      setAttachments((list) => list.filter((x) => x !== a));
    } catch (e) {
      toast.error(logError({ message: 'Could not remove the file', error: e, code: 'SITE_VISIT_DETACH_FAILED' }));
    }
  };

  const checks = rows.filter((r) => r.include && r.title.trim());

  const submit = async () => {
    if (!project) return setError('Choose the project.');
    if (!engineer) return setError('Choose who is visiting.');
    if (!date) return setError('Choose the visit date.');
    if (!purposeName) return setError('Choose the purpose of the visit.');
    if (!checks.length) return setError('The checklist needs at least one item.');
    setSaving(true);
    setError(null);
    const body = {
      jobId: project.id,
      engineerId: engineer.id,
      visitBy,
      purpose: purposeName,
      stages: stages.join(', ') || undefined,
      scheduledDate: date,
      shift,
      instructions: instructions.trim() || undefined,
      checks: checks.map((r) => ({ stage: r.stage, title: r.title.trim(), hint: r.hint })),
    };
    try {
      const saved = visit
        ? await siteVisitsApi.update(visit.id, body)
        : await siteVisitsApi.create({ ...body, attachmentFileIds: attachments.map((a) => a.fileId) });
      toast.success(visit ? `Visit ${saved.visitNo} updated` : `Visit ${saved.visitNo} scheduled`);
      onSaved(saved);
    } catch (e) {
      setError(logError({ message: 'Could not save the visit', error: e, code: 'SITE_VISIT_SAVE_FAILED' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth='md'>
      <DialogTitle>{visit ? `Edit visit ${visit.visitNo}` : 'Schedule site visit'}</DialogTitle>
      <DialogContent>
        <Stack gap={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity='error'>{error}</Alert>}

          <Autocomplete
            options={meta?.projects ?? []}
            value={project}
            disabled={editing}
            onChange={(_, p) => {
              setProject(p);
              setStages([]);
            }}
            getOptionLabel={(p) => projectLabel(p.code, p.name, p.location)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(opts, { inputValue }) => {
              const q = inputValue.trim().toLowerCase();
              if (!q) return opts.slice(0, 100);
              return opts
                .filter((p) => [p.code, p.name, p.location, p.siteAddress].some((v) => (v ?? '').toLowerCase().includes(q)))
                .slice(0, 100);
            }}
            renderOption={(props, p) => (
              <Box component='li' {...props} key={p.id}>
                <Stack>
                  <Typography variant='body2' fontWeight={600}>
                    {[p.code, p.name].filter(Boolean).join(' - ')}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {p.location || p.siteAddress || 'No location'}
                  </Typography>
                </Stack>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label='Project' placeholder='Search code, name or location' size='small' />
            )}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' }, gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup size='small' exclusive value={visitBy} onChange={(_, v) => v && setVisitBy(v)}>
              <ToggleButton value='SITE_ENGINEER'>Site engineer</ToggleButton>
              <ToggleButton value='DIRECTOR'>Director</ToggleButton>
            </ToggleButtonGroup>
            <Autocomplete
              options={meta?.engineers ?? []}
              value={engineer}
              onChange={(_, e) => setEngineer(e)}
              getOptionLabel={(e) => `${e.name}${e.code ? ` (${e.code})` : ''}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label='Visiting engineer' size='small' />}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 160px 1fr' }, gap: 2 }}>
            <TextField label='Visit date' type='date' size='small' value={date} onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
            <TextField select label='Time' size='small' value={shift} onChange={(e) => setShift(e.target.value as Shift)}>
              {(meta?.shifts?.length ? meta.shifts : SHIFTS).map((s) => (
                <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
              ))}
            </TextField>
            <TextField select label='Purpose' size='small' value={purpose}
              onChange={(e) => {
                userChanged.current = true;
                setPurpose(e.target.value);
              }}>
              {purposes.map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
              <MenuItem value={OTHER}>Other…</MenuItem>
            </TextField>
          </Box>
          {purpose === OTHER && (
            <TextField label='Purpose of the visit' size='small' value={otherPurpose} onChange={(e) => setOtherPurpose(e.target.value)} />
          )}

          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Stages to inspect {stagesLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
            </Typography>
            {!project ? (
              <Typography variant='body2' color='text.secondary'>Choose the project to see its floors and slabs.</Typography>
            ) : (
              <Stack direction='row' gap={1} flexWrap='wrap'>
                {Array.from(new Set([...stageOptions, ...stages])).map((s) => (
                  <Chip key={s} label={s} size='small' clickable color={stages.includes(s) ? 'primary' : 'default'}
                    variant={stages.includes(s) ? 'filled' : 'outlined'} onClick={() => toggleStage(s)} />
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Checklist · {checks.length} item{checks.length === 1 ? '' : 's'}
            </Typography>
            <Stack gap={0.5} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, maxHeight: 320, overflowY: 'auto' }}>
              {!rows.length && (
                <Typography variant='body2' color='text.secondary' sx={{ p: 1 }}>
                  Pick stages and a purpose to build the checklist, or add items below.
                </Typography>
              )}
              {rows.map((r, i) => (
                <Stack key={r.key} direction='row' alignItems='center' gap={0.5}>
                  <Checkbox size='small' checked={r.include} onChange={(e) => editRow(r.key, { include: e.target.checked })} />
                  <TextField size='small' fullWidth variant='standard' value={r.title}
                    onChange={(e) => editRow(r.key, { title: e.target.value })}
                    helperText={r.hint || undefined}
                    sx={{ opacity: r.include ? 1 : 0.5 }} />
                  <Tooltip title='Move up'>
                    <span>
                      <IconButton size='small' disabled={i === 0} onClick={() => moveRow(i, -1)}>
                        <Icon icon='mdi:arrow-up' width={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Remove'>
                    <IconButton size='small' onClick={() => removeRow(r.key)}>
                      <Icon icon='mdi:close' width={16} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
            <Stack direction='row' gap={1} sx={{ mt: 1 }}>
              <TextField size='small' fullWidth placeholder='Add a checklist item' value={newItem}
                onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())} />
              <Button onClick={addItem} disabled={!newItem.trim()} startIcon={<Icon icon='mdi:plus' />}>Add</Button>
            </Stack>
          </Box>

          <TextField label='Instructions for the engineer' size='small' multiline minRows={2} value={instructions}
            onChange={(e) => setInstructions(e.target.value)} placeholder='What to look at, whom to meet, what to carry' />

          <Box>
            <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
              <Typography variant='subtitle2'>Drawings / attachments</Typography>
              <Button size='small' startIcon={uploading ? <CircularProgress size={14} /> : <Icon icon='mdi:paperclip' />}
                disabled={uploading} onClick={() => fileInput.current?.click()}>
                Attach
              </Button>
              <input ref={fileInput} type='file' hidden multiple accept='image/*,.pdf,.doc,.docx,.xls,.xlsx'
                onChange={(e) => onFiles(e.target.files)} />
            </Stack>
            <Stack direction='row' gap={1} flexWrap='wrap' sx={{ mt: 0.5 }}>
              {attachments.map((a) => (
                <Chip key={`${a.fileId}-${a.rowId ?? ''}`} size='small' icon={<Icon icon='mdi:file-outline' />} label={a.name}
                  component='a' href={a.url} target='_blank' rel='noreferrer' clickable onDelete={() => removeAttachment(a)} />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant='contained' onClick={submit} disabled={saving || uploading}>
          {saving ? 'Saving…' : visit ? 'Save changes' : 'Schedule visit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
